import { HttpClient, HttpParams } from "@angular/common/http";
import { EventEmitter, inject, Injectable, signal } from "@angular/core";
import { FormControl, ValidationErrors } from "@angular/forms";
import { environment } from "@environments/environments";
import { DataFormDefinition, DataFormEvent, DataFormSubmitResponse } from "@shared/components/data-form/data-form.model";
import { HttpQueryParams, JsonValue } from "@shared/models/domain/custom-views.model";
import { Project } from "@shared/models/domain/wms.model";
import { PageResponse } from "@shared/models/response/page-response.model";
import { BehaviorSubject, Observable, of } from "rxjs";
import { map } from "rxjs/operators";

const legacyPropsReviver = (_key: string, value: JsonValue): JsonValue => {
    if (value && typeof value === 'object' && !Array.isArray(value) && 'templateOptions' in value) {
        const { templateOptions, ...field } = value;
        return { ...field, props: field['props'] ?? templateOptions };
    }
    return value;
};

const toCurrentFormFormat = (form: DataFormDefinition): DataFormDefinition => form && JSON.parse(JSON.stringify(form), legacyPropsReviver) as DataFormDefinition;

@Injectable({
    providedIn: 'root',
})
export class DataFormService {
    private http = inject(HttpClient);

    isInvalidQuestionForm = signal<boolean>(false);
    isUploading = signal<boolean>(false);
    signatureUser$ = new BehaviorSubject<string>('');
    private _customerSubscription = new BehaviorSubject<Project[] | null>(null);
    setModel: object;

    get customerSubscription$(): Observable<Project[]> {
        return this._customerSubscription.asObservable();
    }

    onFormEvent$ = new EventEmitter<DataFormEvent>();

    loadForm(formUrl: string, addExtension = true): Observable<DataFormDefinition> {
        let url: string;
        if (addExtension) {
            url = `/assets/forms/${formUrl}.json`;
            return this.http.get<DataFormDefinition>(url).pipe(map(toCurrentFormFormat));
        } else {
            url = environment.apiUrl + formUrl;
            return this.http.get<PageResponse<DataFormDefinition>>(url).pipe(map((data) => {
                return toCurrentFormFormat(data.item);
            }));
        }
    }

    getFormData(url: string, data: { params?: HttpParams | HttpQueryParams }): Observable<Project[]> {
        return this.http.get<PageResponse<Project>>(environment.apiUrl + url, data).pipe(
            map((data) => {
                const ret = data['items'];
                this._customerSubscription.next(ret)
                return ret;
        }));
    }

    postForm<T = DataFormSubmitResponse>(url: string, data: object): Observable<T> {
        return this.http.post<T>(environment.apiUrl + url, data);
    }

    putForm<T = DataFormSubmitResponse>(url: string, data: object): Observable<T> {
        return this.http.put<T>(environment.apiUrl + url, data);
    }

    patchForm<T = DataFormSubmitResponse>(url: string, data: object): Observable<T> {
        return this.http.patch<T>(environment.apiUrl + url, data);
    }

    removeForm<T = DataFormSubmitResponse>(url: string): Observable<T> {
        return this.http.delete<T>(environment.apiUrl + url);
    }

    validateUniqueUsername(control: FormControl): Observable<ValidationErrors> {
        if (control.value) {
            return of({ "usernameUnique": true });
        }
        return of(null);
    }

    validateUniqueEmail(control: FormControl): Observable<ValidationErrors> {
        if (control.value) {
            return of({ "emailUnique": true });
        }
        return of(null);
    }

    validateContactUniqueEmail(control: FormControl): Observable<ValidationErrors> {
        if (control.value) {
            return of({ "contactEmailUnique": true });
        }
        return of(null);
    }
}
